/**
 * PWAInstallModal.js
 * 
 * Modal component for PWA installation instructions.
 * Handles iOS Safari, Android Chrome, and unsupported browser cases.
 */

import React from 'react';

const PWAInstallModal = ({ isOpen, onClose, platform }) => {
  if (!isOpen) return null;

  // iOS Safari instructions
  const renderIOSInstructions = () => (
    <>
      <div style={{
        fontSize: '48px',
        textAlign: 'center',
        marginBottom: '16px'
      }}>
        📲
      </div>
      <h3 style={{
        fontSize: '20px',
        fontWeight: '600',
        textAlign: 'center',
        margin: '0 0 24px 0',
        color: 'var(--text-primary)'
      }}>
        Install SmartPlaite on Your iPhone
      </h3>
      
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
          <span style={{
            background: 'rgba(102, 126, 234, 0.2)',
            color: '#667eea',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '14px',
            flexShrink: 0
          }}>1</span>
          <div>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', fontWeight: '500' }}>
              Tap the Share icon <span style={{ fontSize: '18px' }}>⬆️</span> in Safari
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Located at the bottom of your screen
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
          <span style={{
            background: 'rgba(102, 126, 234, 0.2)',
            color: '#667eea',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '14px',
            flexShrink: 0
          }}>2</span>
          <div>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', fontWeight: '500' }}>
              Scroll down and tap &ldquo;Add to Home Screen&rdquo;
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Look for the <span style={{ fontSize: '16px' }}>➕</span> icon
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{
            background: 'rgba(102, 126, 234, 0.2)',
            color: '#667eea',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '14px',
            flexShrink: 0
          }}>3</span>
          <div>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', fontWeight: '500' }}>
              Confirm &ldquo;SmartPlaite&rdquo;
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Tap &ldquo;Add&rdquo; in the top right corner
            </p>
          </div>
        </div>
      </div>
      
      <div style={{
        background: 'rgba(245, 158, 11, 0.1)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: '8px',
        padding: '12px',
        textAlign: 'center'
      }}>
        <p style={{ margin: 0, fontSize: '13px', color: 'rgba(245, 158, 11, 0.9)' }}>
          ⚠️ Must be using Safari browser
        </p>
      </div>
    </>
  );

  // Android Chrome fallback instructions
  const renderAndroidInstructions = () => (
    <>
      <div style={{
        fontSize: '48px',
        textAlign: 'center',
        marginBottom: '16px'
      }}>
        📲
      </div>
      <h3 style={{
        fontSize: '20px',
        fontWeight: '600',
        textAlign: 'center',
        margin: '0 0 24px 0',
        color: 'var(--text-primary)'
      }}>
        Install SmartPlaite on Your Android
      </h3>
      
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
          <span style={{
            background: 'rgba(52, 211, 153, 0.2)',
            color: '#34d399',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '14px',
            flexShrink: 0
          }}>1</span>
          <div>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', fontWeight: '500' }}>
              Tap the menu icon <span style={{ fontSize: '18px' }}>⋮</span>
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Three dots in the top right corner
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
          <span style={{
            background: 'rgba(52, 211, 153, 0.2)',
            color: '#34d399',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '14px',
            flexShrink: 0
          }}>2</span>
          <div>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', fontWeight: '500' }}>
              Tap &ldquo;Add to Home screen&rdquo;
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Or &ldquo;Install app&rdquo; if available
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{
            background: 'rgba(52, 211, 153, 0.2)',
            color: '#34d399',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '14px',
            flexShrink: 0
          }}>3</span>
          <div>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', fontWeight: '500' }}>
              Tap &ldquo;Install&rdquo;
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Confirm the installation
            </p>
          </div>
        </div>
      </div>
      
      <div style={{
        background: 'rgba(52, 211, 153, 0.1)',
        border: '1px solid rgba(52, 211, 153, 0.3)',
        borderRadius: '8px',
        padding: '12px',
        textAlign: 'center'
      }}>
        <p style={{ margin: 0, fontSize: '13px', color: 'rgba(52, 211, 153, 0.9)' }}>
          ✓ Works best in Chrome browser
        </p>
      </div>
    </>
  );

  // Unsupported browser message
  const renderUnsupportedMessage = () => (
    <>
      <div style={{
        fontSize: '48px',
        textAlign: 'center',
        marginBottom: '16px'
      }}>
        🌐
      </div>
      <h3 style={{
        fontSize: '20px',
        fontWeight: '600',
        textAlign: 'center',
        margin: '0 0 24px 0',
        color: 'var(--text-primary)'
      }}>
        Browser Not Supported
      </h3>
      
      <div style={{
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '12px',
        padding: '20px',
        textAlign: 'center',
        marginBottom: '20px'
      }}>
        <p style={{ margin: '0 0 16px 0', fontSize: '15px', color: 'var(--text-primary)' }}>
          To install SmartPlaite as an app, please open this page in:
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '4px' }}>🍎</div>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '500' }}>
              Safari<br/><span style={{ fontSize: '12px', opacity: 0.7 }}>iPhone</span>
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '4px' }}>🤖</div>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '500' }}>
              Chrome<br/><span style={{ fontSize: '12px', opacity: 0.7 }}>Android</span>
            </p>
          </div>
        </div>
      </div>
      
      <p style={{ 
        margin: 0, 
        fontSize: '13px', 
        color: 'var(--text-secondary)',
        textAlign: 'center'
      }}>
        Copy this URL and paste it in the recommended browser
      </p>
    </>
  );

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'var(--card-bg, #1a1a2e)',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '400px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid rgba(255,255,255,0.1)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {platform === 'ios' && renderIOSInstructions()}
        {platform === 'android' && renderAndroidInstructions()}
        {platform === 'unsupported' && renderUnsupportedMessage()}
        
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '14px',
            marginTop: '20px',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '10px',
            color: 'var(--text-primary)',
            fontSize: '15px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
};

export default PWAInstallModal;
